import { useState } from "react";
import {
  gridFilterModelSelector,
  useGridApiContext,
  useGridRootProps,
  useGridSelector,
} from "@mui/x-data-grid-premium";
import { Flex } from "src/components/Flex.tsx";
import { SelectV2 } from "src/components/SelectV2/SelectV2.tsx";

import type { GridRenderHeaderFilterProps } from "@mui/x-data-grid-premium";
import type { SelectProps } from "src/components/SelectV2/SelectV2.types.ts";
import type {
  HeaderFilterSelectOptions,
  HeaderFilterSelectServerOptions,
} from "src/components/DataGrid/DataGrid.tsx";

import { useSelect } from "src/system/useSelect.tsx";
import { useDataGridStore } from "src/components/DataGrid/DataGridStoreProvider.tsx";
import { arrReplaceAt } from "@exonn/utils";
import { observer } from "mobx-react-lite";
import { mergeFilterModels } from "src/components/DataGrid/mergeFilterModels.ts";
import { useTranslation } from "react-i18next";
import { TranslationNamespaceEnum } from "src/lib/i18next/types.ts";
import { getResourceFieldOptions } from "src/system/getResourceFieldOptions.ts";

export const DataGridSelectHeaderFilterImpl = observer(
  (
    props: GridRenderHeaderFilterProps &
      Pick<
        SelectProps<string>,
        "placeholder" | "allowClearValue" | "optionsMinWidth"
      > & {
        options?: HeaderFilterSelectOptions;
        serverOptions?: HeaderFilterSelectServerOptions;
      },
  ) => {
    const {
      item,
      options: optionsProp,
      placeholder,
      allowClearValue,
      serverOptions,
      optionsMinWidth,
    } = props;
    const apiRef = useGridApiContext();
    const [isOpen, setIsOpen] = useState(false);
    const { t } = useTranslation(TranslationNamespaceEnum.DataGridComponent);
    const store = useDataGridStore();
    const appliedItem = store.appliedFilterModel.items.find(
      (a) => a.field === item.field,
    );
    const rootProps = useGridRootProps();

    const filterModel = useGridSelector(apiRef, gridFilterModelSelector);

    // const applyFilterChanges = useCallback(
    //   (updatedItem: GridFilterItem) => {
    //     if (!updatedItem.value) {
    //       apiRef.current.deleteFilterItem(updatedItem);
    //     } else {
    //       apiRef.current.upsertFilterItem(updatedItem);
    //     }
    //   },
    //   [apiRef],
    // );

    const getDisplayValue = () => {
      if (item.value != null) {
        return String(item.value);
      }
      if (appliedItem?.value != null) {
        return String(appliedItem.value);
      }

      return "";
    };

    const value = getDisplayValue();
    const hasValue = !!value;

    const { options: backendOptions, isLoading: isLoadingServerOptions } =
      useSelect({
        resource: serverOptions ? serverOptions.resource : "",
        sort: serverOptions?.sort,
        include: serverOptions?.include,
        labelProp: serverOptions?.labelProp,
        valueProp: serverOptions?.valueProp,
        disablePagination: true,
        filter: serverOptions?.filter
          ? {
              items: serverOptions?.filter,
            }
          : undefined,
        queryOptions: {
          enabled: !!serverOptions && (isOpen || hasValue),
        },
      });

    const handleSelect = (value?: string | number) => {
      const nextItem = {
        ...item,
        value: value,
      };
      const mergedFilterModel = mergeFilterModels(
        filterModel,
        store.appliedFilterModel,
      );
      const prevItems = mergedFilterModel.items;

      const nextItems = arrReplaceAt(
        prevItems,
        (prevItem) => prevItem.field === nextItem.field,
        nextItem,
        true,
      );

      const filteredItems = nextItems.filter((item) => !!item.value);

      const nextFilterModel = {
        ...mergedFilterModel,
        items: filteredItems,
      };

      if (rootProps.filterMode === "server") {
        //applyFilterChanges(nextItem);
        apiRef.current.setFilterModel({
          items: [],
        });
        store.setAppliedFilterModel(nextFilterModel);
      } else {
        apiRef.current.setFilterModel(nextFilterModel);
      }
    };

    const options = serverOptions
      ? serverOptions.processOptions
        ? serverOptions.processOptions(backendOptions)
        : backendOptions
      : Array.isArray(optionsProp)
      ? optionsProp
      : optionsProp &&
        getResourceFieldOptions({
          ...optionsProp,
          t,
        });

    return (
      <Flex style={styles.container}>
        <SelectV2
          allowClearValue={allowClearValue}
          onChange={handleSelect}
          onOpenChange={(open) => setIsOpen(open)}
          isLoadingSelectedOption={isLoadingServerOptions}
          isLoadingData={isLoadingServerOptions}
          placeholder={placeholder ?? t("header-filters.select-placeholder")}
          value={value}
          options={options}
          optionsMinWidth={optionsMinWidth}
        />
      </Flex>
    );
  },
);

const styles = {
  container: {
    // 1px padding is needed to not cut off input outline
    padding: "1px 10px 10px",
    width: "100%",
    height: "100%",
  },
};
